import type { TabsTabProps } from '@mantine/core';
import { Tabs as MantineTabs } from '@mantine/core';
import type { ForwardedRef } from 'react';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { slugify } from '../../utils/slugify';
import MantineTooltip from '../mantine-tooltip';
import { TabNotification } from './notification';

interface TabProps extends TabsTabProps {
  notificationCount?: number;
  isDotNotification?: boolean;
  disabledTooltipLabel?: string;
}

// ref required for mantine tooltip
const Tab = forwardRef(
  (
    {
      notificationCount,
      isDotNotification = false,
      disabledTooltipLabel,
      disabled,
      ...rest
    }: TabProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    return (
      <MantineTooltip
        label={disabledTooltipLabel}
        disabled={!disabled || !disabledTooltipLabel}
      >
        <StyledTab
          data-testid={slugify(rest.value)}
          ref={ref}
          disabled={disabled}
          rightSection={
            notificationCount ? (
              <TabNotification
                count={notificationCount}
                isDotNotification={isDotNotification}
              />
            ) : undefined
          }
          {...rest}
        />
      </MantineTooltip>
    );
  }
);

Tab.displayName = 'Tab';

export default Tab;

const StyledTab = styled(MantineTabs.Tab)`
  ${({ theme }) => css`
    box-sizing: border-box;
    border-color: ${theme.palette.neutral[50]};

    &[data-active] {
      border-color: ${theme.palette.neutral[50]};
      color: ${theme.palette.primary.main};
      ::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: ${theme.palette.primary.main};
        margin-left: ${theme.spacing(1)};
        margin-right: ${theme.spacing(1)};
      }
    }
    &:first-child::after {
      margin-left: ${theme.spacing(0)};
    }
    &:last-child::after {
      margin-right: ${theme.spacing(0)};
    }
    &[data-disabled] {
      color: ${theme.palette.neutral[70]};
    }
    &:focus-visible {
      outline: 2px solid ${theme.palette.focus};
      outline-offset: 1px;
    }
    border-radius: 0;
    border-width: 0 0 2px 0;
    &:hover {
      background-color: transparent;
      border-color: ${theme.palette.neutral[50]};
    }
    color: ${theme.palette.neutral[90]};
    padding: ${theme.spacing(0, 1, 2, 1)};
    &:first-child {
      padding: ${theme.spacing(0, 1, 2, 0)};
    }
    &:last-child:not(:first-child) {
      padding: ${theme.spacing(0, 0, 2, 1)};
    }
    ${theme.typography.bodyMd2}
  `}
`;
